import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit} from '@angular/core';
import {Widget} from '@ispm/widgets/decorator/widget.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {DynamicFormItem} from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import {
  IArrayAppenderPayload
} from '@ispm/widgets/components/array-appender/interfaces/array-appender-payload.interface';
import {ITEMS_TYPES} from '@ispm/dynamic-form/constants/ItemsTypes';
import {FormGroup} from '@angular/forms';
import {get, has, findIndex, set} from 'lodash';
import {IdynamicFormItem} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import {IcodeDisplay} from "@ispm-client/modules/aggregator/interfaces";
import {AgregationService} from "@ispm-client/modules/aggregator/services";
import {Store} from '@ngrx/store';

@Component({
  selector: 'ispm-array-appender',
  templateUrl: './array-appender.component.html',
  styleUrls: ['./array-appender.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('ArrayAppender')
export class ArrayAppenderComponent implements OnInit, IWidgetItem<IArrayAppenderPayload> {
  private static DEFAULT_CSS_CLASSES: string = 'full-size-item hide-invalid-flag ';
  widgetUpdateValueCallback: (value) => void;
  public widgetConfigPayload: IArrayAppenderPayload;
  public data: Array<any> [];
  public searchObject: any;
  public restForm: boolean = false;
  public inputConfig: [DynamicFormItem];
  public widgetFormInstance: FormGroup;
  public formCssClasses: string;
  public showSelectionField: boolean = true;
  public store$: Store<any>;
  public btnDisabled: boolean;
  forceUpdate: (value) => void;
  addWidgetFormControl: (config: IdynamicFormItem) => void;
  private autoPopulate: boolean;
  private publishSelection: boolean;
  private resetFormAfterAdding: boolean;
  public disabledStatus: boolean = false;
  private allowEmptyList = true;

  constructor(private _zone: NgZone, private _changeDetector: ChangeDetectorRef,
              private _aggregatorService: AgregationService, private store: Store<any>) {
  }

  public _additionalSearchValues: any;

  get additionalSearchValues() {
    return {...this.widgetFormInstance.value, ...this.widgetConfigPayload.additionalSearchValues};
  }

  set additionalSearchValues(value) {
    this._additionalSearchValues = value;
  }

  set widgetConfig(widgetConfig) {
    this.widgetConfigPayload = widgetConfig.widgetPayload;
    this.autoPopulate = !this.widgetConfigPayload.addButton;
    this.publishSelection = get(this.widgetConfigPayload.searchInput, 'publishSelection', false);
    this.resetFormAfterAdding = get(this.widgetConfigPayload.addButton, 'resetFormAfterAdding', true);
    this.formCssClasses = get(this.widgetConfigPayload, 'formCssClasses', '');
    const cssClasses = `${ArrayAppenderComponent.DEFAULT_CSS_CLASSES} ${get(this.widgetConfigPayload, 'itemCssClasses', '')}`;
    const attributes = Object.assign({}, this.widgetConfigPayload.attributes, {'[forceFormMode]': true});
    this.showSelectionField = get(this.widgetConfigPayload, 'showSelectionField', true);

    let searchInput = DynamicFormItem.builder(
      this.widgetConfigPayload.searchInput.label || '',
      'search',
      this.widgetConfigPayload.searchInput.type || ITEMS_TYPES.AUTOCOMPLETE
    ).setCssClass(cssClasses).setAttributes(attributes);

    if (!this.widgetConfigPayload.searchInput.options && has(widgetConfig, 'options.values')) {
      try {
        let options: Array<IcodeDisplay> = get(widgetConfig, 'options.values', []);
        if (options && options.length > 0)
          searchInput.setOptions(options);
      } catch (err) {
      }
    }

    if (has(this.widgetConfigPayload.searchInput, 'formInstanceDefaultValuePath') && searchInput.options?.values && searchInput.options?.values.length > 0 && this.widgetFormInstance?.value) {
      let valuePath = get(this.widgetConfigPayload.searchInput, 'formInstanceDefaultValuePath');
      let valueToSearch = get(this.widgetFormInstance.value, valuePath);
      if (valueToSearch) {
        let idx = findIndex(searchInput.options.values, function (obj) {
          return obj["code"] === valueToSearch;
        });

        if (idx > -1) {
          searchInput.setDefault(searchInput.options.values[idx]);
          this.searchObject = {"search": searchInput.options.values[idx]};
          this.btnDisabled = this.getButtonDisabledStatus(searchInput.options.values[idx], this.widgetConfigPayload);
        } else
          this.btnDisabled = this.getButtonDisabledStatus(null, this.widgetConfigPayload);
      }
    }

    this.allowEmptyList = get(this.widgetConfigPayload, 'allowEmptyList', false);
    this.inputConfig = [Object.assign(searchInput, this.widgetConfigPayload.searchInput, this.widgetConfigPayload.itemOverride)];
  }

  get widgetData(): any {
    return this.data;
  }

  set widgetData(widgetData: any) {
  const condition1 = !(widgetData === null || widgetData === undefined || (widgetData?.length === 0 && widgetData?.action === undefined));
  const condition2 = widgetData !== null && widgetData !== undefined && widgetData?.length === 0 && this.allowEmptyList;
    if (condition1 || condition2) {
      this._changeDetector.detach();
      this.data = widgetData;
      set(this.data, 'action', get(widgetData, 'action', 'unknown'));
      if (!this.showSelectionField) {
        this.updateDestData();
      }
      this._changeDetector.reattach();
    }
  }

  ngOnInit() {
    if (!this.data) {
      this.data = [];
    }
  }

  getButtonDisabledStatus(selectedValue: any, configuration: any): boolean {
    if (selectedValue) {
      if (configuration?.addButton?.allowAddingDuplicated === false) {
        let objCodeValue = configuration?.addButton?.dataCodeValueMapFrom || "code";
        return findIndex(this.data, function (obj) {
          return obj[objCodeValue] && obj[objCodeValue] === selectedValue["code"];
        }) > -1;
      } else
        return false;
    }

    return true;
  }

  updateSearchObject(form) {
    this.searchObject = form;
    if (this.autoPopulate) {
      this.updateDestArray();
    }

    this.btnDisabled = this.getButtonDisabledStatus(this.searchObject?.search, this.widgetConfigPayload);

    if (this.publishSelection) {
      this.widgetUpdateValueCallback({
        "action": "itemSelected",
        "data": this.searchObject?.search
      });
    }
  }

  updateDestArray() {
    if (this.searchObject && this.searchObject.search) {
      this.data = [...this.data, this.searchObject.search];
      if (this.resetFormAfterAdding)
        this.restForm = true;

      this._changeDetector.markForCheck();
      this.widgetUpdateValueCallback(this.data);
      this.searchObject = undefined;
      this.btnDisabled = this.getButtonDisabledStatus(null, this.widgetConfigPayload);
      this._zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.restForm = false;
          this._changeDetector.markForCheck();
        }, 500);
      });
    }
  }

  updateDestData() {
    if (this.resetFormAfterAdding)
      this.restForm = true;
    this._changeDetector.markForCheck();
    this.updateValue(this.data);
  }

  updateValue($event) {
    switch ($event.action) {
      case 'initiateData': {
        this.data = $event.data;
        this.btnDisabled = this.getButtonDisabledStatus(this.searchObject?.search, this.widgetConfigPayload);
        this._changeDetector.markForCheck();
        break;
      }
      case 'updateData': {
        this.data = $event.data;
        if ($event.resetSelection) {
          this.btnDisabled = this.getButtonDisabledStatus(null, this.widgetConfigPayload);
        }
        this.widgetUpdateValueCallback($event);
        this._changeDetector.markForCheck();
        break;
      }
      case 'itemRemoved': {
        this.data = $event.data.nodes;
        this.btnDisabled = this.getButtonDisabledStatus(null, this.widgetConfigPayload);
        this.widgetUpdateValueCallback($event);
        this._changeDetector.markForCheck();
        break;
      }
      case 'itemSelected': {
        this.widgetUpdateValueCallback($event);
        break;
      }
      default: {
        this.forceUpdate(true);
        this.widgetUpdateValueCallback($event);
        break;
      }
    }
  }

  get updatedFieldsContext() {
    return "";
  }

  setDisabledStatus(disabledStatus: boolean) {
    this.disabledStatus = disabledStatus;
    this._changeDetector.markForCheck();
  }
}
